// src/pages/admin/AdminExams.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Button, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Switch,
  FormControlLabel, Chip, CircularProgress, InputAdornment,
  Tooltip, alpha,
} from "@mui/material";
import DeleteIcon   from "@mui/icons-material/Delete";
import EditIcon     from "@mui/icons-material/Edit";
import AddIcon      from "@mui/icons-material/Add";
import SearchIcon   from "@mui/icons-material/Search";
import SaveIcon     from "@mui/icons-material/Save";
import CloseIcon    from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuizIcon     from "@mui/icons-material/Quiz";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText } from "../../theme";
import PaginationBar from "../../components/PaginationBar";

const EMPTY = { title: "", description: "", duration_minutes: 30, per_question_seconds: 0, is_published: false, pass_threshold: 50, max_attempts: "", max_tab_switches: "", available_from: "", available_until: "", woo_course_code: "", woo_course_id: "", is_free: true };

export default function AdminExams() {
  const toast = useToast();
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search,   setSearch]   = useState("");
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, page_size: pageSize });
      if (search) p.set("search", search);
      const { data } = await API.get(`/exams/?${p}`);
      setItems(data.items || []); setTotal(data.total || 0); setPages(data.pages || 1);
    } catch { toast.error("Impossible de charger les examens."); }
    finally { setLoading(false); }
  }, [page, pageSize, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = (ex) => {
    setEditing(ex);
    setForm({ title: ex.title || "", description: ex.description || "", duration_minutes: ex.duration_minutes ?? 30, per_question_seconds: ex.per_question_seconds ?? 0, is_published: !!ex.is_published, pass_threshold: ex.pass_threshold ?? 50, max_attempts: ex.max_attempts ?? "", max_tab_switches: ex.max_tab_switches ?? "", available_from: ex.available_from ? ex.available_from.slice(0,16) : "", available_until: ex.available_until ? ex.available_until.slice(0,16) : "", woo_course_code: ex.woo_course_code || "", woo_course_id: ex.woo_course_id || "", is_free: ex.is_free ?? true });
    setOpen(true);
  };

  const onSave = async () => {
    if (!form.title?.trim()) { toast.error("Le titre est requis."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["max_attempts","max_tab_switches","woo_course_id","pass_threshold"].forEach(k => {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]) || null;
      });
      if (editing) await API.put(`/exams/${editing.id}`, payload);
      else         await API.post("/exams/", payload);
      toast.success(editing ? "Examen mis à jour ✓" : "Examen créé ✓");
      setOpen(false); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${d.loc?.slice(-1)[0] || "champ"} : ${d.msg}`).join(" | ")
        : (typeof detail === "string" ? detail : "Erreur de sauvegarde.");
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet examen et toutes ses questions ?")) return;
    try { await API.delete(`/exams/${id}`); toast.success("Examen supprimé."); load(); }
    catch { toast.error("Suppression impossible."); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Box>
      <Stack direction={{ xs:"column", sm:"row" }} justifyContent="space-between" alignItems={{ sm:"center" }} sx={{ mb:4 }} spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">GESTION</Typography>
          <Typography variant="h4" fontWeight={700}><Box component="span" sx={gradientText}>Examens</Box></Typography>
          <Typography variant="caption" color="text.secondary">{total} examen{total !== 1 ? "s" : ""}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nouvel examen</Button>
      </Stack>

      <Paper sx={{ p:2, mb:3 }}>
        <TextField placeholder="Rechercher par titre…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width: { xs:"100%", sm:320 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color:"text.secondary" }} /></InputAdornment> }} />
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display:"flex", justifyContent:"center", py:8 }}><CircularProgress /></Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Titre</TableCell>
                  <TableCell align="center">Durée</TableCell>
                  <TableCell align="center">Questions</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py:6, color:"text.secondary" }}>
                    Aucun examen. <Button size="small" onClick={openCreate}>Créer le premier</Button>
                  </TableCell></TableRow>
                ) : items.map(ex => (
                  <TableRow key={ex.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{ex.title}</Typography>
                      {ex.description && <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth:300, display:"block" }}>{ex.description}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip icon={<AccessTimeIcon sx={{ fontSize:"0.85rem !important" }} />} label={`${ex.duration_minutes} min`} size="small"
                        sx={{ background: alpha(C.indigo[500],.08), color: C.indigo[700], fontWeight:600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip icon={<QuizIcon sx={{ fontSize:"0.85rem !important" }} />} label={ex.question_count ?? "—"} size="small"
                        sx={{ background: alpha(C.teal[500],.08), color: C.teal[700], fontWeight:600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={ex.is_published ? "Publié" : "Brouillon"} size="small"
                        sx={{ fontWeight:700, background: ex.is_published ? alpha(C.green[500],.12) : alpha(C.slate[400],.12), color: ex.is_published ? C.green[600] : C.slate[500] }} />
                      {" "}
                      <Chip label={ex.is_free ? "Gratuit" : "Payant"} size="small"
                        sx={{ fontWeight:700, background: ex.is_free ? alpha(C.slate[400],.12) : alpha(C.indigo[500],.12), color: ex.is_free ? C.slate[500] : C.indigo[600] }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifier"><IconButton size="small" onClick={() => openEdit(ex)} sx={{ color:C.indigo[500] }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Supprimer"><IconButton size="small" onClick={() => onDelete(ex.id)} sx={{ color:C.red[500] }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ px:2, pb:1 }}>
              <PaginationBar page={page} pages={pages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={p => { setPageSize(p); setPage(1); }} loading={loading} />
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
        <DialogTitle sx={{ fontWeight:800, display:"flex", alignItems:"center", justifyContent:"space-between", pb:1 }}>
          {editing ? "Modifier l'examen" : "Nouvel examen"}
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display:"flex", flexDirection:"column", gap:2, pt:2.5 }}>
          <TextField label="Titre *" fullWidth value={form.title} onChange={e => set("title", e.target.value)} />
          <TextField label="Description" fullWidth multiline minRows={2} value={form.description} onChange={e => set("description", e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Durée totale (min)" type="number" fullWidth value={form.duration_minutes}
              onChange={e => set("duration_minutes", Number(e.target.value))} inputProps={{ min:1, max:480 }} />
            <TextField label="Temps / question (sec, 0 = illimité)" type="number" fullWidth value={form.per_question_seconds}
              onChange={e => set("per_question_seconds", Number(e.target.value))} inputProps={{ min:0 }} />
          </Stack>
          <TextField label="Limite de tentatives (0 = illimité)" type="number" fullWidth
            value={form.max_attempts ?? ""}
            onChange={e => {
              const v = e.target.value === "" ? "" : Math.max(0, Number(e.target.value));
              set("max_attempts", v === 0 || v === "" ? null : v);
            }}
            inputProps={{ min:0 }}
            helperText="Nombre maximum de fois qu'un candidat peut passer cet examen. Laisser vide = illimité." />
          <TextField label="Changements d'onglet autorisés (0 = désactivé)" type="number" fullWidth
            value={form.max_tab_switches ?? ""}
            onChange={e => {
              const v = e.target.value === "" ? "" : Math.max(0, Number(e.target.value));
              set("max_tab_switches", v === 0 || v === "" ? null : v);
            }}
            inputProps={{ min:0 }}
            helperText="Nb de fois que le candidat peut quitter l'onglet. Dépassé = soumission automatique. Laisser vide = désactivé." />
          <TextField label="Seuil de réussite (%)" type="number" fullWidth
            value={form.pass_threshold ?? 50}
            onChange={e => set("pass_threshold", Math.min(100, Math.max(1, Number(e.target.value))))}
            inputProps={{ min:1, max:100 }}
            helperText="Score minimum requis pour valider l'examen (défaut : 50%)" />
          <Stack direction={{ xs:"column", sm:"row" }} spacing={2}>
            <TextField label="Disponible à partir du" type="datetime-local" fullWidth
              value={form.available_from || ""}
              onChange={e => set("available_from", e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              helperText="Laisser vide = disponible immédiatement" />
            <TextField label="Disponible jusqu'au" type="datetime-local" fullWidth
              value={form.available_until || ""}
              onChange={e => set("available_until", e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              helperText="Laisser vide = pas de date limite" />
          </Stack>
          {/* ── WooCommerce / SSO ── */}
          <Stack direction={{ xs:"column", sm:"row" }} spacing={2}>
            <TextField label="Code cours WooCommerce" fullWidth size="small"
              value={form.woo_course_code || ""}
              onChange={e => set("woo_course_code", e.target.value)}
              placeholder="ex: EXAMONLINE_ACCESS"
              helperText="Correspond au course_code dans le mapping plugin WP" />
            <TextField label="ID cours LearnDash" type="number" fullWidth size="small"
              value={form.woo_course_id || ""}
              onChange={e => { const v = e.target.value; set("woo_course_id", v === "" ? null : parseInt(v, 10) || null); }}
              placeholder="ex: 3202"
              helperText="Pour complétion automatique après réussite" />
          </Stack>
          <FormControlLabel control={<Switch checked={!!form.is_published} onChange={e => set("is_published", e.target.checked)} />}
            label="Publié (visible par les étudiants)" />
          <FormControlLabel control={<Switch checked={!!form.is_free} onChange={e => set("is_free", e.target.checked)} />}
            label="Accès gratuit (sinon, requiert un achat WooCommerce pour être visible/accessible)" />
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
          <Button onClick={() => setOpen(false)} variant="outlined" disabled={saving}>Annuler</Button>
          <Button onClick={onSave} variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
            {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer l'examen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}